import React, { useState, useEffect } from 'react';
import api from '../services/api';
import DeviceHistory from './DeviceHistory';
import ImportExportPanel from './ImportExportPanel';
import SectorsManagement from './SectorsManagement';

const MAX_TELEGRAM_BOTS = 3;

const TELEGRAM_ALERT_TYPES = [
  { key: 'offline', label: 'Dispositivo Offline' },
  { key: 'online', label: 'Dispositivo Online' },
  { key: 'outOfRange', label: 'Valor Fuera de Rango' },
  { key: 'boolean', label: 'Estado ON/OFF en alerta' },
  { key: 'normalized', label: 'Vuelta a normal' },
  { key: 'periodicSummary', label: 'Resumen 9:00 y 18:00' }
];

const newTelegramBot = (number) => ({
  enabled: true,
  botToken: '',
  chatId: '',
  botName: `Bot ${number}`,
  alertTypes: {
    offline: true,
    online: false,
    outOfRange: true,
    boolean: true,
    normalized: true,
    periodicSummary: true
  },
  silentMode: false
});

// Bots del sitio para el formulario. Igual que Site.getTelegramBots en el backend:
// un sitio sin telegramBots pero con telegramConfig legacy se muestra como "Bot 1".
const getSiteTelegramBots = (site) => {
  if (Array.isArray(site?.telegramBots) && site.telegramBots.length > 0) {
    return site.telegramBots.map(bot => ({
      ...newTelegramBot(1),
      ...bot,
      alertTypes: { ...newTelegramBot(1).alertTypes, ...(bot.alertTypes || {}) }
    }));
  }
  const legacy = site?.telegramConfig;
  if (legacy?.botToken) {
    const types = legacy.alertTypes || {};
    return [{
      // Mismo ID estable que usa el backend (el del sitio)
      _id: site._id,
      enabled: !!legacy.enabled,
      botToken: legacy.botToken,
      chatId: legacy.chatId || '',
      botName: legacy.botName || 'IoT Alert Bot',
      alertTypes: {
        offline: types.offline !== false,
        online: types.online === true,
        outOfRange: types.outOfRange !== false,
        boolean: types.outOfRange !== false,
        normalized: types.normalized !== false,
        periodicSummary: types.periodicSummary !== false
      },
      silentMode: !!legacy.silentMode
    }];
  }
  return [];
};

const getActiveTelegramBots = (site) => getSiteTelegramBots(site).filter(bot => bot.enabled);

const Configuration = ({ user: currentUser }) => {
  const isAdmin = currentUser?.role === 'admin';
  const isOperator = currentUser?.role === 'operator';
  const [activeTab, setActiveTab] = useState(isAdmin ? 'users' : 'devices');
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [devices, setDevices] = useState([]);

  // Helper para contar comandos habilitados desde devicePermissions
  const getEnabledCommandsCount = (user) => {
    if (!user.devicePermissions || typeof user.devicePermissions !== 'object') {
      return 0;
    }

    let totalCommands = 0;
    Object.keys(user.devicePermissions).forEach(deviceId => {
      const devicePerms = user.devicePermissions[deviceId];
      if (devicePerms && devicePerms.commands && Array.isArray(devicePerms.commands)) {
        totalCommands += devicePerms.commands.length;
      }
    });

    return totalCommands;
  };

  // Helper para contar magnitudes visibles desde devicePermissions
  const getVisibleMagnitudesCount = (user) => {
    if (!user.devicePermissions || typeof user.devicePermissions !== 'object') {
      return 0;
    }

    let totalMagnitudes = 0;
    Object.keys(user.devicePermissions).forEach(deviceId => {
      const devicePerms = user.devicePermissions[deviceId];
      if (devicePerms && devicePerms.magnitudes && Array.isArray(devicePerms.magnitudes)) {
        totalMagnitudes += devicePerms.magnitudes.length;
      }
    });

    return totalMagnitudes;
  };
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  
  const [userForm, setUserForm] = useState({
    username: '', email: '', password: '', role: 'viewer', assignedSites: [], devicePermissions: {}
  });
  
  const [siteForm, setSiteForm] = useState({
    name: '', description: '', location: { city: '', country: 'Argentina', coordinates: { lat: '', lng: '' } },
    offlineThresholdMinutes: 60,
    telegramBots: []
  });

  // Estado para validación y prueba de Telegram, por índice de bot
  const [telegramValidation, setTelegramValidation] = useState({});
  const [telegramTesting, setTelegramTesting] = useState(null);
  
  const [deviceForm, setDeviceForm] = useState({
    deviceId: '', 
    name: '', 
    type: 'sensor', 
    site: '', 
    mqttTopic: '',
    // Soporte para múltiples magnitudes
    magnitudes: [
      {
        name: 'value1',
        label: 'Magnitud 1',
        jsonPath: '', // Ej: "temperature" o "data.temp"
        unit: '°C',
        decimals: 1,
        minNormalValue: '',
        maxNormalValue: '',
        valueType: 'numeric',
        icon: '📊' // Icono por defecto
      }
    ],
    // NUEVO: Comandos configurables
    commands: []
  });

  // Lista de iconos disponibles para las magnitudes
  const availableIcons = [
    { value: '📊', label: '📊 Genérico' },
    { value: '🌡️', label: '🌡️ Temperatura' },
    { value: '💧', label: '💧 Humedad' },
    { value: '🌬️', label: '🌬️ Presión/Viento' },
    { value: '🔋', label: '🔋 Batería' },
    { value: '💡', label: '💡 Luz/Luminosidad' },
    { value: '⚡', label: '⚡ Voltaje/Potencia' },
    { value: '🔌', label: '🔌 Corriente' },
    { value: '💨', label: '💨 Velocidad' },
    { value: '📏', label: '📏 Distancia' },
    { value: '⚖️', label: '⚖️ Peso' },
    { value: '🧪', label: '🧪 pH/Químicos' },
    { value: '🔊', label: '🔊 Sonido' },
    { value: '🏃', label: '🏃 Movimiento/PIR' },
    { value: '🚪', label: '🚪 Puerta/Ventana' },
    { value: '🔥', label: '🔥 Calor' },
    { value: '❄️', label: '❄️ Frío' },
    { value: '🌊', label: '🌊 Agua' },
    { value: '🌪️', label: '🌪️ Turbulencia' },
    { value: '📈', label: '📈 Tendencia' },
    { value: '⏱️', label: '⏱️ Tiempo' },
    { value: '📍', label: '📍 Ubicación' },
    { value: '🔴', label: '🔴 Estado/Alerta' },
    { value: '🟢', label: '🟢 OK/Normal' },
    { value: '⭐', label: '⭐ Calidad' }
  ];

  // Lista de iconos disponibles para comandos
  const availableCommandIcons = [
    { value: '🎮', label: '🎮 Control' },
    { value: '🔴', label: '🔴 Parar/Apagar' },
    { value: '🟢', label: '🟢 Iniciar/Encender' },
    { value: '⏸️', label: '⏸️ Pausar' },
    { value: '⏯️', label: '⏯️ Play/Pause' },
    { value: '🔄', label: '🔄 Reiniciar' },
    { value: '🔧', label: '🔧 Configurar' },
    { value: '⚙️', label: '⚙️ Ajustes' },
    { value: '🔑', label: '🔑 Acceso' },
    { value: '🚨', label: '🚨 Alarma' },
    { value: '💡', label: '💡 Iluminar' },
    { value: '🔊', label: '🔊 Sonido' },
    { value: '🔇', label: '🔇 Silenciar' },
    { value: '📤', label: '📤 Enviar' },
    { value: '💾', label: '💾 Guardar' },
    { value: '🗑️', label: '🗑️ Eliminar' },
    { value: '🔃', label: '🔃 Actualizar' },
    { value: '⬆️', label: '⬆️ Subir' },
    { value: '⬇️', label: '⬇️ Bajar' },
    { value: '▶️', label: '▶️ Ejecutar' },
    { value: '⏹️', label: '⏹️ Detener' }
  ];

  // Colores disponibles para botones de comandos
  const buttonColors = [
    { value: 'primary', label: 'Azul (Primary)' },
    { value: 'success', label: 'Verde (Success)' },
    { value: 'warning', label: 'Amarillo (Warning)' },
    { value: 'danger', label: 'Rojo (Danger)' },
    { value: 'info', label: 'Celeste (Info)' }
  ];

  // Helper para obtener los IDs de dispositivos permitidos para el operador
  // Considera tanto devicePermissions como assignedSites
  const getOperatorAllowedDeviceIds = (allDevices = []) => {
    const allowedIds = new Set();

    // 1. Agregar dispositivos de devicePermissions
    if (currentUser?.devicePermissions) {
      Object.entries(currentUser.devicePermissions)
        .filter(([_, perm]) => perm && perm.allowed === true)
        .forEach(([deviceId, _]) => allowedIds.add(deviceId));
    }

    // 2. Agregar dispositivos de los sitios asignados
    if (currentUser?.assignedSites && currentUser.assignedSites.length > 0) {
      const assignedSiteIds = currentUser.assignedSites.map(s =>
        typeof s === 'string' ? s : s._id
      );
      allDevices.forEach(device => {
        const deviceSiteId = device.site?._id || device.site;
        if (assignedSiteIds.includes(deviceSiteId)) {
          allowedIds.add(device._id);
        }
      });
    }

    return Array.from(allowedIds);
  };

  useEffect(() => {
    fetchData();
  }, []);


  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, sitesRes, devicesRes] = await Promise.all([
        api.get('/api/users').catch(() => ({data: []})),
        api.get('/api/sites').catch(() => ({data: []})),
        api.get('/api/devices').catch(() => ({data: []}))
      ]);

      console.log('📥 Datos recibidos del backend:');
      console.log('👥 Usuarios completos:', usersRes.data);
      console.log('👥 DevicePermissions específicos:', usersRes.data.map(u => ({
        username: u.username,
        devicePermissions: u.devicePermissions,
        hasDevicePermissions: u.hasOwnProperty('devicePermissions'),
        allKeys: Object.keys(u)
      })));

      // Si es operador, filtrar dispositivos solo a los que tiene acceso
      let filteredDevices = devicesRes.data;
      if (isOperator) {
        const allowedDeviceIds = getOperatorAllowedDeviceIds(devicesRes.data);
        filteredDevices = devicesRes.data.filter(d => allowedDeviceIds.includes(d._id));
        console.log('🔐 Operator allowedDeviceIds:', allowedDeviceIds);
        console.log('🔐 Operator assignedSites:', currentUser?.assignedSites);
        console.log('🔐 Operator devicePermissions:', currentUser?.devicePermissions);
      }

      setUsers(usersRes.data);
      setSites(sitesRes.data);
      setDevices(filteredDevices);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setShowModal(true);
    
    if (item) {
      // Editing existing item
      if (type === 'user') {
        console.log('📝 Abriendo modal para editar usuario:', item);
        console.log('🔐 DevicePermissions del usuario:', item.devicePermissions);
        console.log('🔍 Tipo de devicePermissions:', typeof item.devicePermissions);
        console.log('🔍 Es objeto vacío:', Object.keys(item.devicePermissions || {}).length === 0);

        // FORZAR devicePermissions si no existe
        const devicePermissions = item.devicePermissions || {};

        // Si no tiene devicePermissions, inicializar automáticamente en el backend
        if (!item.devicePermissions || typeof item.devicePermissions === 'undefined') {
          console.log('⚠️ Usuario sin devicePermissions - Inicializando automáticamente...');

          // Actualizar en backend inmediatamente
          api.put(`/api/users/${item._id}`, {
            ...item,
            devicePermissions: {}
          }).then(() => {
            console.log('✅ DevicePermissions inicializado en backend');
          }).catch(err => {
            console.error('❌ Error inicializando devicePermissions:', err);
          });
        }

        const newUserForm = {
          username: item.username,
          email: item.email || '',
          password: '',
          role: item.role,
          assignedSites: item.assignedSites?.map(s => s._id || s) || [],
          devicePermissions: devicePermissions
        };

        console.log('📋 UserForm que se va a establecer:', newUserForm);
        setUserForm(newUserForm);

        // Verificar que se estableció correctamente
        setTimeout(() => {
          console.log('✅ UserForm después del setState (verificación)');
        }, 100);
      } else if (type === 'site') {
        // Resetear estado de validación de Telegram
        setTelegramValidation({});
        setTelegramTesting(null);

        setSiteForm({
          name: item.name,
          description: item.description || '',
          location: {
            city: item.location?.city || '',
            country: item.location?.country || 'Argentina',
            coordinates: {
              lat: item.location?.coordinates?.lat || '',
              lng: item.location?.coordinates?.lng || ''
            }
          },
          offlineThresholdMinutes: item.offlineThresholdMinutes || 60,
          telegramBots: getSiteTelegramBots(item)
        });
      } else if (type === 'device') {
        // Si el dispositivo tiene magnitudes guardadas, usarlas
        const magnitudes = item.magnitudes && item.magnitudes.length > 0
          ? item.magnitudes.map(mag => ({
              ...mag,
              icon: mag.icon || '📊', // Asegurar que todas las magnitudes tengan icono
              // Asegurar campos booleanos con valores por defecto
              normalState: mag.normalState || 'true',
              normalColor: mag.normalColor || '#10b981',
              abnormalColor: mag.abnormalColor || '#ef4444',
              showInDashboard: mag.showInDashboard !== false
            }))
          : [
              {
                name: 'value1',
                label: item.name || 'Magnitud 1',
                jsonPath: '',
                unit: item.unit || '',
                decimals: item.decimals || 1,
                minNormalValue: item.minNormalValue || '',
                maxNormalValue: item.maxNormalValue || '',
                valueType: item.valueType || 'numeric',
                icon: '📊',
                // Campos para magnitudes booleanas
                normalState: 'true',
                normalColor: '#10b981',
                abnormalColor: '#ef4444',
                showInDashboard: true
              }
            ];

        // Asegurar que los comandos también tengan showInDashboard
        const commands = (item.commands || []).map(cmd => ({
          ...cmd,
          showInDashboard: cmd.showInDashboard !== false
        }));
            
        setDeviceForm({
          deviceId: item.deviceId,
          name: item.name,
          type: item.type,
          site: item.site?._id || item.site || '',
          mqttTopic: item.mqttTopic || '',
          magnitudes: magnitudes,
          commands: commands
        });
      }
    } else {
      // Creating new item - reset forms
      setUserForm({ username: '', email: '', password: '', role: 'viewer', assignedSites: [], devicePermissions: {} });
      setTelegramValidation({});
      setTelegramTesting(null);
      setSiteForm({
        name: '',
        description: '',
        location: { city: '', country: 'Argentina', coordinates: { lat: '', lng: '' } },
        offlineThresholdMinutes: 60,
        telegramBots: []
      });
      setDeviceForm({
        deviceId: '',
        name: '',
        type: 'sensor',
        site: '',
        mqttTopic: '',
        magnitudes: [
          {
            name: 'value1',
            label: 'Magnitud 1',
            jsonPath: '',
            unit: '',
            decimals: 1,
            minNormalValue: '',
            maxNormalValue: '',
            valueType: 'numeric',
            icon: '📊',
            // Campos para magnitudes booleanas
            normalState: 'true', // 'true' para ON normal, 'false' para OFF normal
            normalColor: '#10b981', // Verde por defecto para estado normal
            abnormalColor: '#ef4444', // Rojo por defecto para estado anormal
            showInDashboard: true
          }
        ],
        commands: []
      });
    }
  };

  const duplicateItem = (type, item) => {
    // Crear una copia profunda para evitar referencias compartidas
    const duplicatedItem = JSON.parse(JSON.stringify(item));

    // Remove the _id to create a new item
    delete duplicatedItem._id;

    // Modify the name/identifier to indicate it's a copy
    if (type === 'user') {
      duplicatedItem.username = `${item.username}_copy`;
      duplicatedItem.email = `copy_${item.email}`;
    } else if (type === 'site') {
      duplicatedItem.name = `${item.name} (Copia)`;
    } else if (type === 'device') {
      duplicatedItem.name = `${item.name} (Copia)`;
      duplicatedItem.deviceIdMqtt = `${item.deviceIdMqtt}_copy`;
    }

    console.log(`📋 Duplicando ${type}:`, duplicatedItem);

    // Open modal with duplicated item
    setModalType(type);
    setEditingItem(duplicatedItem);
    setShowModal(true);
  };

  const addMagnitude = () => {
    const newMagnitude = {
      name: `value${deviceForm.magnitudes.length + 1}`,
      label: `Magnitud ${deviceForm.magnitudes.length + 1}`,
      jsonPath: '',
      unit: '',
      decimals: 1,
      minNormalValue: '',
      maxNormalValue: '',
      valueType: 'numeric',
      icon: '📊',
      // Campos para magnitudes booleanas
      normalState: 'true',
      normalColor: '#10b981',
      abnormalColor: '#ef4444',
      // Visibilidad en dashboard
      showInDashboard: true
    };
    setDeviceForm({
      ...deviceForm,
      magnitudes: [...deviceForm.magnitudes, newMagnitude]
    });
  };

  const removeMagnitude = (index) => {
    if (deviceForm.magnitudes.length > 1) {
      const newMagnitudes = deviceForm.magnitudes.filter((_, i) => i !== index);
      setDeviceForm({
        ...deviceForm,
        magnitudes: newMagnitudes
      });
    }
  };

  // Función para generar un nombre único para magnitudes
  const generateUniqueMagnitudeName = (baseName, existingMagnitudes) => {
    const existingNames = existingMagnitudes.map(m => m.name);

    // Eliminar sufijos _copy o _N existentes para obtener el nombre base real
    let cleanBaseName = baseName.replace(/_copy(_copy)*$/, '').replace(/_\d+$/, '');

    // Si el nombre base ya termina en _copy, limpiarlo
    if (cleanBaseName.endsWith('_copy')) {
      cleanBaseName = cleanBaseName.replace(/_copy$/, '');
    }

    // Buscar el siguiente número disponible
    let counter = 2;
    let newName = `${cleanBaseName}_${counter}`;

    while (existingNames.includes(newName)) {
      counter++;
      newName = `${cleanBaseName}_${counter}`;
    }

    return newName;
  };

  // Función para duplicar magnitud
  const duplicateMagnitude = (index) => {
    const magnitudeToDuplicate = deviceForm.magnitudes[index];
    // Crear una copia profunda para evitar referencias compartidas
    const newMagnitude = JSON.parse(JSON.stringify(magnitudeToDuplicate));

    // Generar nombre único
    newMagnitude.name = generateUniqueMagnitudeName(magnitudeToDuplicate.name, deviceForm.magnitudes);
    newMagnitude.label = `${magnitudeToDuplicate.label} (Copia)`;

    const newMagnitudes = [...deviceForm.magnitudes];
    newMagnitudes.splice(index + 1, 0, newMagnitude);

    setDeviceForm({
      ...deviceForm,
      magnitudes: newMagnitudes
    });
  };

  // Funciones para manejar comandos
  const addCommand = () => {
    const newCommand = {
      name: `command${deviceForm.commands.length + 1}`,
      label: `Comando ${deviceForm.commands.length + 1}`,
      description: '',
      mqttTopic: `devices/${deviceForm.deviceId}/commands`,
      payload: { action: 'example', value: true },
      buttonColor: 'primary',
      icon: '🎮',
      confirmRequired: false,
      confirmMessage: '¿Estás seguro de ejecutar este comando?',
      showInDashboard: true
    };
    setDeviceForm({
      ...deviceForm,
      commands: [...deviceForm.commands, newCommand]
    });
  };

  const removeCommand = (index) => {
    const newCommands = deviceForm.commands.filter((_, i) => i !== index);
    setDeviceForm({
      ...deviceForm,
      commands: newCommands
    });
  };

  // Función para generar un nombre único para comandos
  const generateUniqueCommandName = (baseName, existingCommands) => {
    const existingNames = existingCommands.map(c => c.name);

    // Eliminar sufijos _copy o _N existentes para obtener el nombre base real
    let cleanBaseName = baseName.replace(/_copy(_copy)*$/, '').replace(/_\d+$/, '');

    // Si el nombre base ya termina en _copy, limpiarlo
    if (cleanBaseName.endsWith('_copy')) {
      cleanBaseName = cleanBaseName.replace(/_copy$/, '');
    }

    // Buscar el siguiente número disponible
    let counter = 2;
    let newName = `${cleanBaseName}_${counter}`;

    while (existingNames.includes(newName)) {
      counter++;
      newName = `${cleanBaseName}_${counter}`;
    }

    return newName;
  };

  // Función para duplicar comando
  const duplicateCommand = (index) => {
    const commandToDuplicate = deviceForm.commands[index];
    // Crear una copia profunda para evitar referencias compartidas
    const newCommand = JSON.parse(JSON.stringify(commandToDuplicate));

    // Generar nombre único
    newCommand.name = generateUniqueCommandName(commandToDuplicate.name, deviceForm.commands);
    newCommand.label = `${commandToDuplicate.label} (Copia)`;

    const newCommands = [...deviceForm.commands];
    newCommands.splice(index + 1, 0, newCommand);

    setDeviceForm({
      ...deviceForm,
      commands: newCommands
    });
  };

  const updateCommand = (index, field, value) => {
    const newCommands = [...deviceForm.commands];
    if (field === 'payload') {
      try {
        // Validar que sea JSON válido
        newCommands[index][field] = JSON.parse(value);
      } catch (error) {
        // Si no es JSON válido, guardar como string para que el usuario pueda corregirlo
        newCommands[index][field] = value;
      }
    } else {
      newCommands[index][field] = value;
    }
    setDeviceForm({
      ...deviceForm,
      commands: newCommands
    });
  };

  const updateMagnitude = (index, field, value) => {
    const newMagnitudes = [...deviceForm.magnitudes];
    newMagnitudes[index][field] = value;
    setDeviceForm({
      ...deviceForm,
      magnitudes: newMagnitudes
    });
  };

  // Habilita/deshabilita un bot de Telegram para las alarmas de una magnitud
  const toggleMagnitudeBot = (index, botId, enabled) => {
    const current = (deviceForm.magnitudes[index].telegramDisabledBots || []).map(String);
    const next = enabled
      ? current.filter(id => id !== String(botId))
      : [...new Set([...current, String(botId)])];
    updateMagnitude(index, 'telegramDisabledBots', next);
  };

  // Bots de Telegram del sitio elegido en el formulario de dispositivo
  const deviceSiteBots = getSiteTelegramBots(
    Array.isArray(sites) ? sites.find(s => s._id === deviceForm.site) : null
  ).filter(bot => bot._id);

  const openHistory = (deviceId) => {
    setSelectedDeviceId(deviceId);
    setShowHistory(true);
  };

  // Funciones para Telegram (cada una opera sobre el bot en la posición `index`)
  const setBotValidation = (index, value) => {
    setTelegramValidation(prev => ({ ...prev, [index]: value }));
  };

  const validateTelegramToken = async (index) => {
    const bot = siteForm.telegramBots[index];
    if (!bot?.botToken) {
      alert('Ingresa el token del bot primero');
      return;
    }

    setBotValidation(index, { validating: true, valid: null, botInfo: null, error: null });

    try {
      // Usar ruta genérica que no requiere ID de sitio
      const response = await api.post('/api/sites/telegram/validate', {
        botToken: bot.botToken
      });

      setBotValidation(index, {
        validating: false,
        valid: true,
        botInfo: response.data.botInfo,
        error: null
      });
    } catch (error) {
      setBotValidation(index, {
        validating: false,
        valid: false,
        botInfo: null,
        error: error.response?.data?.message || 'Token inválido'
      });
    }
  };

  const sendTelegramTest = async (index) => {
    const bot = siteForm.telegramBots[index];
    if (!bot?.botToken || !bot?.chatId) {
      alert('Ingresa el token del bot y el Chat ID primero');
      return;
    }

    setTelegramTesting(index);

    try {
      // Usar ruta genérica que no requiere ID de sitio
      await api.post('/api/sites/telegram/test', {
        botToken: bot.botToken,
        chatId: bot.chatId,
        siteName: siteForm.name || 'Nuevo Sitio'
      });

      alert('Mensaje de prueba enviado correctamente. Revisa tu chat de Telegram.');
    } catch (error) {
      alert(error.response?.data?.message || 'Error al enviar mensaje de prueba');
    } finally {
      setTelegramTesting(null);
    }
  };

  const updateTelegramBot = (index, field, value) => {
    setSiteForm({
      ...siteForm,
      telegramBots: siteForm.telegramBots.map((bot, i) =>
        i === index ? { ...bot, [field]: value } : bot
      )
    });
    // Si cambió el token, la validación anterior ya no aplica
    if (field === 'botToken') setBotValidation(index, undefined);
  };

  const updateTelegramAlertType = (index, alertType, value) => {
    setSiteForm({
      ...siteForm,
      telegramBots: siteForm.telegramBots.map((bot, i) =>
        i === index ? { ...bot, alertTypes: { ...bot.alertTypes, [alertType]: value } } : bot
      )
    });
  };

  const addTelegramBot = () => {
    if (siteForm.telegramBots.length >= MAX_TELEGRAM_BOTS) return;
    setSiteForm({
      ...siteForm,
      telegramBots: [...siteForm.telegramBots, newTelegramBot(siteForm.telegramBots.length + 1)]
    });
  };

  const removeTelegramBot = (index) => {
    const bot = siteForm.telegramBots[index];
    if (!window.confirm(`¿Eliminar el bot "${bot.botName || `Bot ${index + 1}`}"? Se aplica al guardar el sitio.`)) return;
    setSiteForm({
      ...siteForm,
      telegramBots: siteForm.telegramBots.filter((_, i) => i !== index)
    });
    // Los índices se corren: descartar validaciones previas
    setTelegramValidation({});
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      // Si es operador, forzar rol a 'viewer'
      const formToSubmit = isOperator ? { ...userForm, role: 'viewer' } : userForm;
      console.log('📝 Guardando usuario - datos a enviar:', formToSubmit);

      if (editingItem) {
        const updateData = { ...formToSubmit };
        if (!updateData.password) {
          delete updateData.password;
        }
        console.log('🔄 Actualizando usuario existente:', editingItem._id, updateData);
        const response = await api.put(`/api/users/${editingItem._id}`, updateData);
        console.log('✅ Usuario actualizado - respuesta:', response.data);
        alert('Usuario actualizado exitosamente');
      } else {
        console.log('🆕 Creando nuevo usuario:', formToSubmit);
        const response = await api.post('/api/users', formToSubmit);
        console.log('✅ Usuario creado - respuesta:', response.data);
        alert('Usuario creado exitosamente');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('❌ Error al guardar usuario:', error);
      console.error('❌ Error response:', error.response?.data);
      alert(error.response?.data?.message || 'Error al guardar usuario');
    }
  };

  const handleSiteSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/api/sites/${editingItem._id}`, siteForm);
        alert('Sitio actualizado exitosamente');
      } else {
        await api.post('/api/sites', siteForm);
        alert('Sitio creado exitosamente');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || 'Error al guardar sitio');
    }
  };

  const handleDeviceSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validar que no hay nombres de magnitudes duplicados
      const magnitudeNames = deviceForm.magnitudes.map(m => m.name);
      const duplicateMagnitudeNames = magnitudeNames.filter((name, index) => magnitudeNames.indexOf(name) !== index);
      if (duplicateMagnitudeNames.length > 0) {
        alert(`Error: Hay nombres de magnitudes duplicados: ${[...new Set(duplicateMagnitudeNames)].join(', ')}. Cada magnitud debe tener un nombre único.`);
        return;
      }

      // Validar que no hay nombres de comandos duplicados
      const commandNames = deviceForm.commands.map(c => c.name);
      const duplicateCommandNames = commandNames.filter((name, index) => commandNames.indexOf(name) !== index);
      if (duplicateCommandNames.length > 0) {
        alert(`Error: Hay nombres de comandos duplicados: ${[...new Set(duplicateCommandNames)].join(', ')}. Cada comando debe tener un nombre único.`);
        return;
      }

      // Preparar los datos con las magnitudes Y comandos
      const data = {
        deviceId: deviceForm.deviceId,
        name: deviceForm.name,
        type: deviceForm.type,
        site: deviceForm.site,
        mqttTopic: deviceForm.mqttTopic || `devices/${deviceForm.deviceId}/+`,
        magnitudes: deviceForm.magnitudes.map(mag => ({
          name: mag.name,
          label: mag.label,
          jsonPath: mag.jsonPath,
          unit: mag.unit,
          decimals: parseInt(mag.decimals),
          minNormalValue: mag.minNormalValue ? parseFloat(mag.minNormalValue) : null,
          maxNormalValue: mag.maxNormalValue ? parseFloat(mag.maxNormalValue) : null,
          valueType: mag.valueType,
          icon: mag.icon || '📊',
          // Campos para magnitudes booleanas
          normalState: mag.normalState || 'true',
          normalColor: mag.normalColor || '#10b981',
          abnormalColor: mag.abnormalColor || '#ef4444',
          // Visibilidad en dashboard
          showInDashboard: mag.showInDashboard !== false,
          // Bots de Telegram que NO reciben las alarmas de esta magnitud
          telegramDisabledBots: Array.isArray(mag.telegramDisabledBots) ? mag.telegramDisabledBots : []
        })),
        // IMPORTANTE: Incluir comandos
        commands: deviceForm.commands.map(cmd => ({
          name: cmd.name,
          label: cmd.label,
          description: cmd.description || '',
          mqttTopic: cmd.mqttTopic,
          payload: cmd.payload,
          buttonColor: cmd.buttonColor || 'primary',
          icon: cmd.icon || '🎮',
          confirmRequired: cmd.confirmRequired || false,
          confirmMessage: cmd.confirmMessage || '¿Estás seguro?',
          showInDashboard: cmd.showInDashboard !== false
        }))
      };
      
      if (editingItem) {
        await api.put(`/api/devices/${editingItem._id}`, data);
        alert('Dispositivo actualizado exitosamente');
      } else {
        await api.post('/api/devices', data);
        alert('Dispositivo creado exitosamente');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || 'Error al guardar dispositivo');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('¿Está seguro de eliminar?')) return;
    
    try {
      if (type === 'user') await api.delete(`/api/users/${id}`);
      if (type === 'site') await api.delete(`/api/sites/${id}`);
      if (type === 'device') await api.delete(`/api/devices/${id}`);
      
      alert('Eliminado exitosamente');
      fetchData();
    } catch (error) {
      alert('Error al eliminar');
    }
  };

  const editButtonStyle = {
    padding: '0.25rem 0.5rem',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.5rem'
  };

  const deleteButtonStyle = {
    padding: '0.25rem 0.5rem',
    background: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  };

  const duplicateButtonStyle = {
    padding: '0.25rem 0.5rem',
    background: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.5rem'
  };

  const historyButtonStyle = {
    padding: '0.25rem 0.5rem',
    background: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.5rem'
  };

  const resetPasswordButtonStyle = {
    padding: '0.25rem 0.5rem',
    background: '#f59e0b',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.5rem'
  };

  const pinButtonStyle = {
    padding: '0.25rem 0.5rem',
    background: '#8b5cf6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.5rem'
  };

  // Función para configurar PIN de preferencias
  const handleConfigurePin = async (user) => {
    // Primero verificar si ya tiene PIN
    try {
      const checkResponse = await api.get(`/api/users/${user._id}/has-pin`);
      const hasPin = checkResponse.data.hasPin;

      const message = hasPin
        ? `El usuario ${user.username} YA tiene un PIN configurado.\n\nIngrese el nuevo PIN (4-6 dígitos) o deje vacío para ELIMINAR el PIN:`
        : `Configurar PIN de acceso a preferencias para ${user.username}.\n\nIngrese un PIN de 4-6 dígitos:`;

      const newPin = prompt(message);

      // Si cancela el prompt
      if (newPin === null) return;

      // Si ingresa vacío, eliminar PIN
      if (newPin === '') {
        if (hasPin) {
          const confirmDelete = window.confirm('¿Está seguro que desea eliminar el PIN de este usuario?');
          if (!confirmDelete) return;
        } else {
          return; // No había PIN y no ingresó nada
        }
      } else {
        // Validar formato del PIN
        if (!/^\d{4,6}$/.test(newPin)) {
          alert('El PIN debe tener entre 4 y 6 dígitos numéricos');
          return;
        }
      }

      const response = await api.put(`/api/users/${user._id}/preferences-pin`, {
        pin: newPin || null
      });
      alert(response.data.message || 'PIN configurado exitosamente');
    } catch (error) {
      console.error('Error configuring PIN:', error);
      alert(error.response?.data?.message || 'Error al configurar PIN');
    }
  };

  // Función para resetear contraseña
  const handleResetPassword = async (user) => {
    const newPassword = prompt(`Ingrese la nueva contraseña para ${user.username}:\n(mínimo 6 caracteres)`);

    if (!newPassword) return;

    if (newPassword.length < 6) {
      alert('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      const response = await api.post(`/api/users/${user._id}/reset-password`, { newPassword });
      alert(response.data.message || 'Contraseña actualizada exitosamente');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(error.response?.data?.message || 'Error al resetear contraseña');
    }
  };

  if (loading) return <div style={{ padding: '2rem' }}>Cargando...</div>;

  return (
    <div style={{ padding: '2rem' }}>
      <h1>{isOperator ? 'Mi Configuración' : 'Configuración del Sistema'}</h1>

      <div style={{
        marginBottom: '2rem',
        display: 'flex',
        gap: '0.5rem',
        flexWrap: 'wrap',
        padding: '0.5rem',
        background: '#f1f5f9',
        borderRadius: '8px'
      }}>
        <button
          onClick={() => setActiveTab('devices')}
          style={{
            padding: '0.75rem 1.25rem',
            background: activeTab === 'devices' ? '#2563eb' : 'transparent',
            color: activeTab === 'devices' ? 'white' : '#475569',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: activeTab === 'devices' ? '600' : '500',
            fontSize: '0.95rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s ease'
          }}
        >
          <span style={{ fontSize: '1.1rem' }}>📱</span>
          Dispositivos
          <span style={{
            background: activeTab === 'devices' ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
            padding: '0.15rem 0.5rem',
            borderRadius: '10px',
            fontSize: '0.8rem',
            fontWeight: '600'
          }}>{devices.length}</span>
        </button>
        <button
          onClick={() => setActiveTab('users')}
          style={{
            padding: '0.75rem 1.25rem',
            background: activeTab === 'users' ? '#2563eb' : 'transparent',
            color: activeTab === 'users' ? 'white' : '#475569',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: activeTab === 'users' ? '600' : '500',
            fontSize: '0.95rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s ease'
          }}
        >
          <span style={{ fontSize: '1.1rem' }}>👥</span>
          Usuarios
          <span style={{
            background: activeTab === 'users' ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
            padding: '0.15rem 0.5rem',
            borderRadius: '10px',
            fontSize: '0.8rem',
            fontWeight: '600'
          }}>{users.length}</span>
        </button>
        {isAdmin && (
          <button
            onClick={() => setActiveTab('sites')}
            style={{
              padding: '0.75rem 1.25rem',
              background: activeTab === 'sites' ? '#2563eb' : 'transparent',
              color: activeTab === 'sites' ? 'white' : '#475569',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === 'sites' ? '600' : '500',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease'
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>📍</span>
            Sitios
            <span style={{
              background: activeTab === 'sites' ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
              padding: '0.15rem 0.5rem',
              borderRadius: '10px',
              fontSize: '0.8rem',
              fontWeight: '600'
            }}>{sites.length}</span>
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => setActiveTab('sectors')}
            style={{
              padding: '0.75rem 1.25rem',
              background: activeTab === 'sectors' ? '#2563eb' : 'transparent',
              color: activeTab === 'sectors' ? 'white' : '#475569',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === 'sectors' ? '600' : '500',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease'
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>📂</span>
            Sectores
          </button>
        )}
      </div>

      {activeTab === 'sectors' && isAdmin && (
        <SectorsManagement user={currentUser} />
      )}

      {activeTab === 'users' && (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <button onClick={() => openModal('user')} style={{ padding: '0.5rem 1rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              + Nuevo Usuario {isOperator && '(Viewer)'}
            </button>
          </div>
          {isOperator && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
              <span style={{ color: '#0369a1' }}>Puedes crear usuarios tipo Viewer con acceso a tus dispositivos asignados.</span>
            </div>
          )}

          {isAdmin && (
            <ImportExportPanel
              type="users"
              title="Usuarios"
              onImportComplete={fetchData}
            />
          )}
          <table style={{ width: '100%', background: 'white', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Usuario</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Email</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Rol</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Sitios Asignados</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Comandos Habilitados</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Magnitudes Visibles</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user._id}>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{user.username}</td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{user.email || '-'}</td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{user.role}</td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    {user.assignedSites?.map(s => s.name || s).join(', ') || 'Ninguno'}
                  </td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      background: getEnabledCommandsCount(user) > 0 ? '#dcfce7' : '#f3f4f6',
                      color: getEnabledCommandsCount(user) > 0 ? '#166534' : '#6b7280',
                      fontWeight: '500'
                    }}>
                      🎮 {getEnabledCommandsCount(user)} comando{getEnabledCommandsCount(user) !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      background: getVisibleMagnitudesCount(user) > 0 ? '#f0f9ff' : '#f3f4f6',
                      color: getVisibleMagnitudesCount(user) > 0 ? '#0369a1' : '#6b7280',
                      fontWeight: '500'
                    }}>
                      📊 {getVisibleMagnitudesCount(user)} magnitud{getVisibleMagnitudesCount(user) !== 1 ? 'es' : ''}
                    </span>
                  </td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    {isAdmin && (
                      <button onClick={() => duplicateItem('user', user)} style={duplicateButtonStyle}>
                        Duplicar
                      </button>
                    )}
                    <button onClick={() => openModal('user', user)} style={editButtonStyle}>
                      Editar
                    </button>
                    <button onClick={() => handleResetPassword(user)} style={resetPasswordButtonStyle} title="Resetear contraseña">
                      Pass
                    </button>
                    <button onClick={() => handleConfigurePin(user)} style={pinButtonStyle} title="Configurar PIN de preferencias">
                      PIN
                    </button>
                    <button onClick={() => handleDelete('user', user._id)} style={deleteButtonStyle}>
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'sites' && (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <button onClick={() => openModal('site')} style={{ padding: '0.5rem 1rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              + Nuevo Sitio
            </button>
          </div>

          <ImportExportPanel
            type="sites"
            title="Sitios"
            onImportComplete={fetchData}
          />
          <table style={{ width: '100%', background: 'white', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Nombre</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Descripción</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Ciudad</th>
                <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Mapa</th>
                <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Telegram</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sites.map(site => (
                <tr key={site._id}>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{site.name}</td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{site.description || '-'}</td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{site.location?.city || '-'}</td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                    {site.location?.coordinates?.lat && site.location?.coordinates?.lng ? (
                      <button
                        onClick={() => window.open(`https://www.google.com/maps?q=${site.location.coordinates.lat},${site.location.coordinates.lng}&z=15`, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes')}
                        title={`Ver ubicación: ${site.location.coordinates.lat}, ${site.location.coordinates.lng}`}
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: '#059669',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                      >
                        🗺️
                      </button>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                    {getActiveTelegramBots(site).length > 0 ? (
                      <span title={`Bots activos: ${getActiveTelegramBots(site).map(b => b.botName || 'IoT Alert Bot').join(', ')}`} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        background: '#dcfce7',
                        color: '#166534',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        📲 {getActiveTelegramBots(site).length} bot{getActiveTelegramBots(site).length > 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        background: '#f3f4f6',
                        color: '#6b7280',
                        borderRadius: '4px',
                        fontSize: '0.75rem'
                      }}>
                        - No
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    <button onClick={() => duplicateItem('site', site)} style={duplicateButtonStyle}>
                      Duplicar
                    </button>
                    <button onClick={() => openModal('site', site)} style={editButtonStyle}>
                      Editar
                    </button>
                    <button onClick={() => handleDelete('site', site._id)} style={deleteButtonStyle}>
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'devices' && (
        <div>
          {isAdmin && (
            <div style={{ marginBottom: '1rem' }}>
              <button onClick={() => openModal('device')} style={{ padding: '0.5rem 1rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                + Nuevo Dispositivo
              </button>
            </div>
          )}
          {isOperator && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
              <span style={{ color: '#0369a1' }}>Solo puedes editar la configuración de los dispositivos que te han sido asignados.</span>
            </div>
          )}

          {isAdmin && (
            <ImportExportPanel
              type="devices"
              title="Dispositivos"
              onImportComplete={fetchData}
            />
          )}
          <table style={{ width: '100%', background: 'white', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>ID</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Nombre</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Tipo</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Sitio</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Magnitudes</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Estado</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(devices) ? devices : []).map(device => (
                <tr key={device._id}>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{device.deviceId}</td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{device.name}</td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{device.type}</td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{device.site?.name || '-'}</td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    {device.magnitudes?.length > 0 
                      ? device.magnitudes.map(m => m.label).join(', ')
                      : '1 valor'
                    }
                  </td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      background: device.status === 'online' ? '#10b981' : '#ef4444',
                      color: 'white'
                    }}>
                      {device.status || 'offline'}
                    </span>
                  </td>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    <button onClick={() => openHistory(device._id)} style={historyButtonStyle}>
                      Historial
                    </button>
                    {isAdmin && (
                      <button onClick={() => duplicateItem('device', device)} style={duplicateButtonStyle}>
                        Duplicar
                      </button>
                    )}
                    <button onClick={() => openModal('device', device)} style={editButtonStyle}>
                      Editar
                    </button>
                    {isAdmin && (
                      <button onClick={() => handleDelete('device', device._id)} style={deleteButtonStyle}>
                        Eliminar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', width: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2>
              {editingItem ? 'Editar' : 'Nuevo'} {modalType === 'user' ? 'Usuario' : modalType === 'site' ? 'Sitio' : 'Dispositivo'}
            </h2>
            
            {modalType === 'user' && (
              <form onSubmit={handleUserSubmit}>
                <input 
                  type="text" 
                  placeholder="Usuario" 
                  value={userForm.username} 
                  onChange={e => setUserForm({...userForm, username: e.target.value})} 
                  required 
                  style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                />
                <input 
                  type="email" 
                  placeholder="Email" 
                  value={userForm.email} 
                  onChange={e => setUserForm({...userForm, email: e.target.value})} 
                  style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                />
                <input 
                  type="password" 
                  placeholder={editingItem ? "Nueva contraseña (dejar vacío para no cambiar)" : "Contraseña"} 
                  value={userForm.password} 
                  onChange={e => setUserForm({...userForm, password: e.target.value})} 
                  required={!editingItem} 
                  style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                />
                {isAdmin ? (
                  <select
                    value={userForm.role}
                    onChange={e => setUserForm({...userForm, role: e.target.value})}
                    style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="operator">Operator</option>
                    <option value="admin">Admin</option>
                  </select>
                ) : (
                  <div style={{ marginBottom: '1rem', padding: '0.5rem', background: '#f3f4f6', borderRadius: '4px' }}>
                    <span style={{ fontWeight: 'bold' }}>Rol: </span>
                    <span>Viewer (los operadores solo pueden crear usuarios viewer)</span>
                  </div>
                )}

                {isAdmin && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Sitios Asignados:</label>
                    <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '0.5rem', maxHeight: '150px', overflowY: 'auto' }}>
                      {sites.map(site => (
                        <div key={site._id} style={{ marginBottom: '0.25rem' }}>
                          <label>
                            <input
                              type="checkbox"
                              value={site._id}
                              checked={userForm.assignedSites.includes(site._id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setUserForm({...userForm, assignedSites: [...userForm.assignedSites, site._id]});
                                } else {
                                  setUserForm({...userForm, assignedSites: userForm.assignedSites.filter(id => id !== site._id)});
                                }
                              }}
                              style={{ marginRight: '0.5rem' }}
                            />
                            {site.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}


                {/* Sección de configuración de permisos por dispositivo */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Dispositivos Permitidos y sus Permisos:
                    {isOperator && <span style={{ fontWeight: 'normal', color: '#6b7280', fontSize: '0.875rem' }}> (solo tus dispositivos asignados)</span>}
                  </label>
                  <div style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '1rem',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    background: '#f8fafc'
                  }}>
                    {devices.length > 0 ? (
                      devices.map(device => {
                        const deviceId = device._id;
                        const devicePermissions = userForm.devicePermissions[deviceId] || { allowed: false, magnitudes: [], commands: [] };

                        return (
                          <div key={deviceId} style={{
                            marginBottom: '1rem',
                            padding: '1rem',
                            background: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}>
                            {/* Checkbox principal del dispositivo */}
                            <div style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={devicePermissions.allowed}
                                  onChange={(e) => {
                                    console.log(`🔄 Cambiando permisos para dispositivo ${device.name}:`, e.target.checked);
                                    const newDevicePermissions = { ...userForm.devicePermissions };
                                    if (e.target.checked) {
                                      newDevicePermissions[deviceId] = {
                                        allowed: true,
                                        magnitudes: device.magnitudes?.map(m => m.name) || [],
                                        commands: device.commands?.map(c => c.name) || []
                                      };
                                    } else {
                                      newDevicePermissions[deviceId] = {
                                        allowed: false,
                                        magnitudes: [],
                                        commands: []
                                      };
                                    }
                                    console.log('📝 Nuevos devicePermissions:', newDevicePermissions);
                                    setUserForm({...userForm, devicePermissions: newDevicePermissions});
                                  }}
                                  style={{ marginRight: '0.75rem', transform: 'scale(1.2)' }}
                                />
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{ fontSize: '1.2rem' }}>📱</span>
                                  <div>
                                    <div style={{ fontWeight: '700', color: '#1f2937', fontSize: '1.05rem' }}>
                                      {device.name}
                                    </div>
                                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                      {device.site?.name || 'Sin sitio'} • {device.deviceId}
                                    </div>
                                  </div>
                                </div>
                              </label>
                            </div>

                            {/* Sección de magnitudes y comandos (solo si el dispositivo está permitido) */}
                            {devicePermissions.allowed && (
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                {/* Magnitudes */}
                                <div>
                                  <h6 style={{ margin: '0 0 0.5rem 0', color: '#4338ca', fontWeight: '600', fontSize: '0.875rem' }}>
                                    📊 Magnitudes Visibles
                                  </h6>
                                  <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '0.5rem', background: '#f9fafb' }}>
                                    {device.magnitudes && device.magnitudes.length > 0 ? (
                                      device.magnitudes.map(magnitude => (
                                        <div key={magnitude.name} style={{ marginBottom: '0.25rem' }}>
                                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.875rem' }}>
                                            <input
                                              type="checkbox"
                                              checked={devicePermissions.magnitudes.includes(magnitude.name)}
                                              onChange={(e) => {
                                                const newDevicePermissions = { ...userForm.devicePermissions };
                                                if (e.target.checked) {
                                                  newDevicePermissions[deviceId].magnitudes = [...devicePermissions.magnitudes, magnitude.name];
                                                } else {
                                                  newDevicePermissions[deviceId].magnitudes = devicePermissions.magnitudes.filter(m => m !== magnitude.name);
                                                }
                                                setUserForm({...userForm, devicePermissions: newDevicePermissions});
                                              }}
                                              style={{ marginRight: '0.5rem' }}
                                            />
                                            <span style={{ marginRight: '0.25rem' }}>{magnitude.icon || '📊'}</span>
                                            <span>{magnitude.label}</span>
                                            {magnitude.unit && (
                                              <span style={{ marginLeft: '0.25rem', color: '#059669', fontSize: '0.75rem' }}>({magnitude.unit})</span>
                                            )}
                                          </label>
                                        </div>
                                      ))
                                    ) : (
                                      <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.75rem', padding: '0.5rem' }}>
                                        Sin magnitudes configuradas
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Comandos */}
                                <div>
                                  <h6 style={{ margin: '0 0 0.5rem 0', color: '#7c2d12', fontWeight: '600', fontSize: '0.875rem' }}>
                                    🎮 Comandos Permitidos
                                  </h6>
                                  <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '0.5rem', background: '#f9fafb' }}>
                                    {device.commands && device.commands.length > 0 ? (
                                      device.commands.map(command => (
                                        <div key={command.name} style={{ marginBottom: '0.25rem' }}>
                                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.875rem' }}>
                                            <input
                                              type="checkbox"
                                              checked={devicePermissions.commands.includes(command.name)}
                                              onChange={(e) => {
                                                const newDevicePermissions = { ...userForm.devicePermissions };
                                                if (e.target.checked) {
                                                  newDevicePermissions[deviceId].commands = [...devicePermissions.commands, command.name];
                                                } else {
                                                  newDevicePermissions[deviceId].commands = devicePermissions.commands.filter(c => c !== command.name);
                                                }
                                                setUserForm({...userForm, devicePermissions: newDevicePermissions});
                                              }}
                                              style={{ marginRight: '0.5rem' }}
                                            />
                                            <span style={{ marginRight: '0.25rem' }}>{command.icon || '🎮'}</span>
                                            <span>{command.label}</span>
                                          </label>
                                        </div>
                                      ))
                                    ) : (
                                      <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.75rem', padding: '0.5rem' }}>
                                        Sin comandos configurados
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div style={{
                        textAlign: 'center',
                        padding: '2rem',
                        color: '#6b7280',
                        fontSize: '0.875rem'
                      }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📱</div>
                        <div>No hay dispositivos configurados</div>
                        <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                          Configura dispositivos antes de asignar permisos
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    marginTop: '0.5rem',
                    background: '#fef7cd',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #fbbf24'
                  }}>
                    🔐 <strong>Permisos por Dispositivo:</strong> Marca los dispositivos que puede acceder este usuario. Para cada dispositivo permitido, selecciona qué magnitudes puede ver y qué comandos puede ejecutar. Esta configuración granular permite un control preciso del acceso a cada funcionalidad.
                  </div>
                </div>

                
                <button type="submit" style={{ padding: '0.5rem 1rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', marginRight: '0.5rem' }}>
                  {editingItem ? 'Actualizar' : 'Crear'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '0.5rem 1rem', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px' }}>
                  Cancelar
                </button>
              </form>
            )}
            
            {modalType === 'site' && (
              <form onSubmit={handleSiteSubmit}>
                <input 
                  type="text" 
                  placeholder="Nombre" 
                  value={siteForm.name} 
                  onChange={e => setSiteForm({...siteForm, name: e.target.value})} 
                  required 
                  style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                />
                <textarea 
                  placeholder="Descripción" 
                  value={siteForm.description} 
                  onChange={e => setSiteForm({...siteForm, description: e.target.value})} 
                  rows="3"
                  style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                />
                <input 
                  type="text" 
                  placeholder="Ciudad" 
                  value={siteForm.location.city} 
                  onChange={e => setSiteForm({...siteForm, location: {...siteForm.location, city: e.target.value}})} 
                  style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                />
                <input
                  type="text"
                  placeholder="País"
                  value={siteForm.location.country}
                  onChange={e => setSiteForm({...siteForm, location: {...siteForm.location, country: e.target.value}})}
                  style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />

                {/* Coordenadas GPS */}
                <div style={{
                  marginBottom: '1rem',
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                  borderRadius: '8px',
                  border: '1px solid #6ee7b7'
                }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#047857', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>📍</span>
                    Ubicación GPS
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#374151', fontSize: '0.875rem' }}>
                        Latitud
                      </label>
                      <input
                        type="number"
                        step="any"
                        placeholder="-34.6037"
                        value={siteForm.location.coordinates?.lat || ''}
                        onChange={e => setSiteForm({
                          ...siteForm,
                          location: {
                            ...siteForm.location,
                            coordinates: {
                              ...siteForm.location.coordinates,
                              lat: e.target.value ? parseFloat(e.target.value) : ''
                            }
                          }
                        })}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#374151', fontSize: '0.875rem' }}>
                        Longitud
                      </label>
                      <input
                        type="number"
                        step="any"
                        placeholder="-58.3816"
                        value={siteForm.location.coordinates?.lng || ''}
                        onChange={e => setSiteForm({
                          ...siteForm,
                          location: {
                            ...siteForm.location,
                            coordinates: {
                              ...siteForm.location.coordinates,
                              lng: e.target.value ? parseFloat(e.target.value) : ''
                            }
                          }
                        })}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const lat = siteForm.location.coordinates?.lat;
                      const lng = siteForm.location.coordinates?.lng;
                      if (lat && lng) {
                        window.open(`https://www.google.com/maps?q=${lat},${lng}&z=15`, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
                      } else {
                        alert('Ingrese las coordenadas (latitud y longitud) para ver la ubicación en el mapa');
                      }
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: siteForm.location.coordinates?.lat && siteForm.location.coordinates?.lng ? '#059669' : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: siteForm.location.coordinates?.lat && siteForm.location.coordinates?.lng ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      width: '100%',
                      justifyContent: 'center'
                    }}
                  >
                    <span>🗺️</span>
                    Ver en Google Maps
                  </button>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                    Tip: Puede obtener las coordenadas desde Google Maps haciendo clic derecho en un punto
                  </p>
                </div>

                {/* Umbral para marcar dispositivos offline */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#374151' }}>
                    Tiempo sin reportar para marcar OFFLINE
                  </label>
                  <select
                    value={siteForm.offlineThresholdMinutes || 60}
                    onChange={e => setSiteForm({ ...siteForm, offlineThresholdMinutes: parseInt(e.target.value, 10) })}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value={15}>15 minutos</option>
                    <option value={30}>30 minutos</option>
                    <option value={60}>1 hora</option>
                    <option value={120}>2 horas</option>
                  </select>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                    Cada dispositivo de este sitio se marcará offline (y disparará alerta) tras este tiempo sin recibir datos.
                  </p>
                </div>

                {/* Sección de Configuración de Telegram (hasta 3 bots, cada uno con sus alarmas) */}
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #e0f2fe, #dbeafe)',
                  borderRadius: '8px',
                  border: '1px solid #93c5fd'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h4 style={{ margin: 0, color: '#1e40af', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>📲</span>
                      Bots de Telegram ({siteForm.telegramBots.length}/{MAX_TELEGRAM_BOTS})
                    </h4>
                    <button
                      type="button"
                      onClick={addTelegramBot}
                      disabled={siteForm.telegramBots.length >= MAX_TELEGRAM_BOTS}
                      style={{
                        padding: '0.4rem 0.8rem',
                        background: '#2563eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: siteForm.telegramBots.length >= MAX_TELEGRAM_BOTS ? 'not-allowed' : 'pointer',
                        opacity: siteForm.telegramBots.length >= MAX_TELEGRAM_BOTS ? 0.5 : 1,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      + Agregar bot
                    </button>
                  </div>

                  <div style={{
                    padding: '0.75rem',
                    background: '#eff6ff',
                    borderRadius: '6px',
                    marginBottom: '1rem',
                    fontSize: '0.875rem',
                    color: '#1e40af'
                  }}>
                    <strong>Instrucciones para crear un bot de Telegram:</strong>
                    <ol style={{ margin: '0.5rem 0 0 1rem', paddingLeft: '0.5rem' }}>
                      <li>Abre Telegram y busca @BotFather</li>
                      <li>Envía /newbot y sigue las instrucciones</li>
                      <li>Copia el token que te proporciona</li>
                      <li>Para obtener el Chat ID, agrega el bot a un grupo o envíale un mensaje y usa @userinfobot</li>
                    </ol>
                    <div style={{ marginTop: '0.5rem' }}>
                      Cada bot recibe solo las alarmas que tenga marcadas. Podés usar el mismo token con distintos Chat ID.
                    </div>
                  </div>

                  {siteForm.telegramBots.length === 0 && (
                    <div style={{ padding: '0.75rem', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
                      Sin bots configurados: este sitio no envía alertas por Telegram.
                    </div>
                  )}

                  {siteForm.telegramBots.map((bot, index) => {
                    const validation = telegramValidation[index] || {};
                    const canTest = telegramTesting === null && bot.botToken && bot.chatId;
                    return (
                      <div key={index} style={{
                        marginBottom: '1rem',
                        padding: '0.75rem',
                        background: 'white',
                        borderRadius: '6px',
                        border: `1px solid ${bot.enabled ? '#93c5fd' : '#e5e7eb'}`,
                        opacity: bot.enabled ? 1 : 0.75
                      }}>
                        {/* Encabezado del bot */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', gap: '0.5rem' }}>
                          <strong style={{ color: '#1e40af' }}>Bot {index + 1}</strong>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={bot.enabled}
                                onChange={(e) => updateTelegramBot(index, 'enabled', e.target.checked)}
                                style={{ transform: 'scale(1.2)' }}
                              />
                              <span style={{ fontWeight: '500', color: bot.enabled ? '#059669' : '#6b7280' }}>
                                {bot.enabled ? 'Habilitado' : 'Deshabilitado'}
                              </span>
                            </label>
                            <button
                              type="button"
                              onClick={() => removeTelegramBot(index)}
                              title="Eliminar bot"
                              style={{ padding: '0.25rem 0.5rem', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>

                        {/* Nombre del Bot */}
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#374151' }}>
                            Nombre (para identificarlo)
                          </label>
                          <input
                            type="text"
                            placeholder="Ej: Guardia, Mantenimiento, Gerencia"
                            value={bot.botName || ''}
                            onChange={(e) => updateTelegramBot(index, 'botName', e.target.value)}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </div>

                        {/* Token del Bot */}
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#374151' }}>
                            Token del Bot
                          </label>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input
                              type="password"
                              placeholder="123456789:ABCdefGHIjklMNOpqrSTUvwxYZ"
                              value={bot.botToken || ''}
                              onChange={(e) => updateTelegramBot(index, 'botToken', e.target.value)}
                              style={{ flex: 1, padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                            <button
                              type="button"
                              onClick={() => validateTelegramToken(index)}
                              disabled={validation.validating}
                              style={{
                                padding: '0.5rem 1rem',
                                background: validation.valid === true ? '#059669' : validation.valid === false ? '#dc2626' : '#6366f1',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: validation.validating ? 'wait' : 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {validation.validating ? 'Validando...' : validation.valid === true ? 'Válido' : validation.valid === false ? 'Inválido' : 'Validar'}
                            </button>
                          </div>
                          {validation.botInfo && (
                            <div style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#059669' }}>
                              Bot válido: @{validation.botInfo.username}
                            </div>
                          )}
                          {validation.error && (
                            <div style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#dc2626' }}>
                              {validation.error}
                            </div>
                          )}
                        </div>

                        {/* Chat ID */}
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#374151' }}>
                            Chat ID (grupo o usuario)
                          </label>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input
                              type="text"
                              placeholder="-1001234567890 o 123456789"
                              value={bot.chatId || ''}
                              onChange={(e) => updateTelegramBot(index, 'chatId', e.target.value)}
                              style={{ flex: 1, padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                            <button
                              type="button"
                              onClick={() => sendTelegramTest(index)}
                              disabled={!canTest}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#059669',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: canTest ? 'pointer' : 'not-allowed',
                                opacity: canTest ? 1 : 0.5,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {telegramTesting === index ? 'Enviando...' : 'Probar'}
                            </button>
                          </div>
                        </div>

                        {/* Alarmas que reenvía este bot */}
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                            Alarmas que reenvía este bot
                          </label>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.5rem' }}>
                            {TELEGRAM_ALERT_TYPES.map(type => (
                              <label key={type.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={bot.alertTypes?.[type.key] === true}
                                  onChange={(e) => updateTelegramAlertType(index, type.key, e.target.checked)}
                                />
                                <span>{type.label}</span>
                              </label>
                            ))}
                          </div>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            "Vuelta a normal" solo se envía para las alarmas de rango / ON-OFF que este bot tenga marcadas.
                          </span>
                        </div>

                        {/* Modo silencioso */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={bot.silentMode || false}
                            onChange={(e) => updateTelegramBot(index, 'silentMode', e.target.checked)}
                          />
                          <span style={{ fontWeight: '500' }}>Modo Silencioso</span>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>(sin notificación sonora)</span>
                        </label>
                      </div>
                    );
                  })}
                </div>

                <button type="submit" style={{ padding: '0.5rem 1rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', marginRight: '0.5rem' }}>
                  {editingItem ? 'Actualizar' : 'Crear'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '0.5rem 1rem', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px' }}>
                  Cancelar
                </button>
              </form>
            )}

            {modalType === 'device' && (
              <form onSubmit={handleDeviceSubmit}>
                {/* Información básica del dispositivo */}
                <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#4b5563' }}>Información Básica</h4>
                  <input 
                    type="text" 
                    placeholder="Device ID (ej: arduino_001)" 
                    value={deviceForm.deviceId} 
                    onChange={e => setDeviceForm({...deviceForm, deviceId: e.target.value})} 
                    required 
                    style={{ width: '100%', padding: '0.5rem', marginBottom: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                  />
                  <input 
                    type="text" 
                    placeholder="Nombre del Dispositivo" 
                    value={deviceForm.name} 
                    onChange={e => setDeviceForm({...deviceForm, name: e.target.value})} 
                    required 
                    style={{ width: '100%', padding: '0.5rem', marginBottom: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                  />
                  <select 
                    value={deviceForm.type} 
                    onChange={e => setDeviceForm({...deviceForm, type: e.target.value})} 
                    style={{ width: '100%', padding: '0.5rem', marginBottom: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="sensor">Sensor</option>
                    <option value="actuator">Actuador</option>
                    <option value="gateway">Gateway</option>
                    <option value="controller">Controlador</option>
                  </select>
                  <select 
                    value={deviceForm.site} 
                    onChange={e => setDeviceForm({...deviceForm, site: e.target.value})} 
                    required 
                    style={{ width: '100%', padding: '0.5rem', marginBottom: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="">Seleccione un sitio</option>
                    {sites.map(site => (
                      <option key={site._id} value={site._id}>{site.name}</option>
                    ))}
                  </select>
                  <input 
                    type="text" 
                    placeholder="MQTT Topic (ej: devices/arduino_001/telemetry)" 
                    value={deviceForm.mqttTopic} 
                    onChange={e => setDeviceForm({...deviceForm, mqttTopic: e.target.value})} 
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                  />
                </div>

                {/* Configuración de Magnitudes */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4 style={{ margin: 0, color: '#4b5563' }}>Magnitudes del Dispositivo</h4>
                    <button 
                      type="button"
                      onClick={addMagnitude}
                      style={{ 
                        padding: '0.25rem 0.75rem', 
                        background: '#10b981', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      + Agregar Magnitud
                    </button>
                  </div>
                  
                  {deviceForm.magnitudes.map((magnitude, index) => (
                    <div key={index} style={{ 
                      marginBottom: '1rem', 
                      padding: '1rem', 
                      background: '#f0f9ff', 
                      borderRadius: '8px',
                      border: '1px solid #bfdbfe'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <h5 style={{ margin: 0, color: '#1e40af' }}>
                            Magnitud {index + 1}
                          </h5>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.75rem',
                            color: magnitude.showInDashboard !== false ? '#059669' : '#9ca3af',
                            cursor: 'pointer',
                            padding: '0.25rem 0.5rem',
                            background: magnitude.showInDashboard !== false ? '#d1fae5' : '#f3f4f6',
                            borderRadius: '4px',
                            border: `1px solid ${magnitude.showInDashboard !== false ? '#a7f3d0' : '#e5e7eb'}`
                          }}>
                            <input
                              type="checkbox"
                              checked={magnitude.showInDashboard !== false}
                              onChange={e => updateMagnitude(index, 'showInDashboard', e.target.checked)}
                              style={{ cursor: 'pointer' }}
                            />
                            Mostrar en Dashboard
                          </label>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            type="button"
                            onClick={() => duplicateMagnitude(index)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: '#06b6d4',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.75rem'
                            }}
                          >
                            📋 Duplicar
                          </button>
                          {deviceForm.magnitudes.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeMagnitude(index)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <input 
                          type="text" 
                          placeholder="Etiqueta (ej: Temperatura)" 
                          value={magnitude.label}
                          onChange={e => updateMagnitude(index, 'label', e.target.value)}
                          required
                          style={{ padding: '0.375rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                        />
                        <input 
                          type="text" 
                          placeholder="Path JSON (ej: temperature o data.temp)" 
                          value={magnitude.jsonPath}
                          onChange={e => updateMagnitude(index, 'jsonPath', e.target.value)}
                          required
                          style={{ padding: '0.375rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                        />
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <input 
                          type="text" 
                          placeholder="Unidad (°C, %, V)" 
                          value={magnitude.unit}
                          onChange={e => updateMagnitude(index, 'unit', e.target.value)}
                          style={{ padding: '0.375rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                        />
                        <input 
                          type="number" 
                          placeholder="Decimales" 
                          min="0" 
                          max="4"
                          value={magnitude.decimals}
                          onChange={e => updateMagnitude(index, 'decimals', e.target.value)}
                          style={{ padding: '0.375rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                        />
                        <select 
                          value={magnitude.valueType}
                          onChange={e => updateMagnitude(index, 'valueType', e.target.value)}
                          style={{ padding: '0.375rem', border: '1px solid #ddd', borderRadius: '4px' }}
                        >
                          <option value="numeric">Numérico</option>
                          <option value="boolean">Booleano</option>
                          <option value="text">Texto</option>
                        </select>
                        <select 
                          value={magnitude.icon || '📊'}
                          onChange={e => updateMagnitude(index, 'icon', e.target.value)}
                          style={{ padding: '0.375rem', border: '1px solid #ddd', borderRadius: '4px' }}
                        >
                          {availableIcons.map(icon => (
                            <option key={icon.value} value={icon.value}>
                              {icon.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {magnitude.valueType === 'numeric' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                          <input 
                            type="number" 
                            step="0.1"
                            placeholder="Valor Mínimo Normal" 
                            value={magnitude.minNormalValue}
                            onChange={e => updateMagnitude(index, 'minNormalValue', e.target.value)}
                            style={{ padding: '0.375rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                          />
                          <input 
                            type="number" 
                            step="0.1"
                            placeholder="Valor Máximo Normal" 
                            value={magnitude.maxNormalValue}
                            onChange={e => updateMagnitude(index, 'maxNormalValue', e.target.value)}
                            style={{ padding: '0.375rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                          />
                        </div>
                      )}
                      
                      {/* Configuración específica para magnitudes booleanas */}
                      {magnitude.valueType === 'boolean' && (
                        <div style={{ 
                          marginTop: '0.75rem',
                          padding: '1rem', 
                          background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', 
                          borderRadius: '8px',
                          border: '1px solid #bbf7d0'
                        }}>
                          <h6 style={{ margin: '0 0 0.75rem 0', color: '#166534', fontWeight: '600' }}>
                            🔘 Configuración Booleana (ON/OFF)
                          </h6>
                          
                          {/* Estado Normal */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#166534' }}>
                                Estado Normal:
                              </label>
                              <select 
                                value={magnitude.normalState || 'true'}
                                onChange={e => updateMagnitude(index, 'normalState', e.target.value)}
                                style={{ 
                                  padding: '0.375rem', 
                                  border: '1px solid #bbf7d0', 
                                  borderRadius: '4px',
                                  background: 'white',
                                  width: '100%'
                                }}
                              >
                                <option value="true">🟢 ON (Activo)</option>
                                <option value="false">🔴 OFF (Inactivo)</option>
                              </select>
                            </div>
                            
                            {/* Color Normal */}
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#166534' }}>
                                Color Normal:
                              </label>
                              <input 
                                type="color" 
                                value={magnitude.normalColor || '#10b981'}
                                onChange={e => updateMagnitude(index, 'normalColor', e.target.value)}
                                style={{ 
                                  padding: '0.1rem', 
                                  border: '1px solid #bbf7d0', 
                                  borderRadius: '4px',
                                  width: '100%',
                                  height: '38px'
                                }}
                              />
                            </div>
                            
                            {/* Color Anormal */}
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#166534' }}>
                                Color Anormal:
                              </label>
                              <input 
                                type="color" 
                                value={magnitude.abnormalColor || '#ef4444'}
                                onChange={e => updateMagnitude(index, 'abnormalColor', e.target.value)}
                                style={{ 
                                  padding: '0.1rem', 
                                  border: '1px solid #bbf7d0', 
                                  borderRadius: '4px',
                                  width: '100%',
                                  height: '38px'
                                }}
                              />
                            </div>
                          </div>
                          
                          {/* Explicación de la configuración */}
                          <div style={{ 
                            padding: '0.75rem', 
                            background: 'rgba(22, 101, 52, 0.1)', 
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            color: '#166534',
                            border: '1px solid rgba(22, 101, 52, 0.2)'
                          }}>
                            <strong>💡 Explicación:</strong><br/>
                            <strong>Estado Normal:</strong> Define si ON o OFF es el estado "correcto" para esta magnitud.<br/>
                            <strong>Color Normal:</strong> Color que se mostrará cuando el dispositivo esté en el estado correcto.<br/>
                            <strong>Color Anormal:</strong> Color que se mostrará cuando el dispositivo NO esté en el estado correcto.<br/>
                            <em>Ejemplo: Si "Estado Normal" es ON, cuando el dispositivo envíe true mostrará el color normal (verde), y cuando envíe false mostrará el color anormal (rojo).</em>
                          </div>
                        </div>
                      )}

                      {/* Bots de Telegram que reciben las alarmas de esta magnitud */}
                      <div style={{
                        marginTop: '0.75rem',
                        padding: '0.75rem',
                        background: '#eff6ff',
                        borderRadius: '6px',
                        border: '1px solid #bfdbfe'
                      }}>
                        <div style={{ fontWeight: '500', color: '#1e40af', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                          📲 Enviar alarmas de esta magnitud a:
                        </div>
                        {!deviceForm.site ? (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Elegí un sitio para ver sus bots de Telegram.</div>
                        ) : deviceSiteBots.length === 0 ? (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>El sitio no tiene bots de Telegram configurados.</div>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem' }}>
                            {deviceSiteBots.map(bot => {
                              const botTypeKey = magnitude.valueType === 'boolean' ? 'boolean' : 'outOfRange';
                              const botReceivesType = bot.alertTypes?.[botTypeKey] === true;
                              const checked = !(magnitude.telegramDisabledBots || []).map(String).includes(String(bot._id));
                              return (
                                <label key={bot._id} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={e => toggleMagnitudeBot(index, bot._id, e.target.checked)}
                                  />
                                  <span style={{ color: bot.enabled ? '#111827' : '#9ca3af' }}>
                                    {bot.botName || 'Bot'}
                                    {!bot.enabled && ' (bot deshabilitado)'}
                                    {bot.enabled && !botReceivesType && (
                                      <span title="Este bot no tiene activado ese tipo de alarma en la configuración del sitio" style={{ color: '#b45309' }}>
                                        {' '}(el bot no recibe {botTypeKey === 'boolean' ? 'ON/OFF' : 'fuera de rango'})
                                      </span>
                                    )}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  <div style={{ 
                    padding: '0.75rem', 
                    background: '#fef3c7', 
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    color: '#92400e'
                  }}>
                    <strong>💡 Ejemplo de JSON Path:</strong><br/>
                    Si tu dispositivo envía: <code>{`{"temperature": 25.5, "humidity": 60}`}</code><br/>
                    Los paths serían: <code>temperature</code> y <code>humidity</code><br/>
                    Si envía: <code>{`{"data": {"temp": 25.5, "hum": 60}}`}</code><br/>
                    Los paths serían: <code>data.temp</code> y <code>data.hum</code><br/>
                    <strong>🎨 Iconos:</strong> Selecciona un icono representativo para cada magnitud que se mostrará en el dashboard.
                  </div>
                </div>

                {/* Configuración de Comandos */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4 style={{ margin: 0, color: '#4b5563' }}>Comandos del Dispositivo</h4>
                    <button 
                      type="button"
                      onClick={addCommand}
                      style={{ 
                        padding: '0.25rem 0.75rem', 
                        background: '#8b5cf6', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      + Agregar Comando
                    </button>
                  </div>
                  
                  {deviceForm.commands.map((command, index) => (
                    <div key={index} style={{ 
                      marginBottom: '1rem', 
                      padding: '1rem', 
                      background: '#fdf4ff', 
                      borderRadius: '8px',
                      border: '1px solid #d8b4fe'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <h5 style={{ margin: 0, color: '#7c3aed' }}>
                            Comando {index + 1}
                          </h5>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.75rem',
                            color: command.showInDashboard !== false ? '#059669' : '#9ca3af',
                            cursor: 'pointer',
                            padding: '0.25rem 0.5rem',
                            background: command.showInDashboard !== false ? '#d1fae5' : '#f3f4f6',
                            borderRadius: '4px',
                            border: `1px solid ${command.showInDashboard !== false ? '#a7f3d0' : '#e5e7eb'}`
                          }}>
                            <input
                              type="checkbox"
                              checked={command.showInDashboard !== false}
                              onChange={e => updateCommand(index, 'showInDashboard', e.target.checked)}
                              style={{ cursor: 'pointer' }}
                            />
                            Mostrar en Dashboard
                          </label>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            type="button"
                            onClick={() => duplicateCommand(index)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: '#06b6d4',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.75rem'
                            }}
                          >
                            📋 Duplicar
                          </button>
                          <button
                            type="button"
                            onClick={() => removeCommand(index)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.75rem'
                            }}
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <input 
                          type="text" 
                          placeholder="Etiqueta (ej: Encender LED)" 
                          value={command.label}
                          onChange={e => updateCommand(index, 'label', e.target.value)}
                          required
                          style={{ padding: '0.375rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                        />
                        <input 
                          type="text" 
                          placeholder="Tópico MQTT" 
                          value={command.mqttTopic}
                          onChange={e => updateCommand(index, 'mqttTopic', e.target.value)}
                          required
                          style={{ padding: '0.375rem', border: '1px solid #ddd', borderRadius: '4px' }} 
                        />
                      </div>
                      
                      <div style={{ marginBottom: '0.5rem' }}>
                        <textarea 
                          placeholder='JSON a enviar (ej: {"action": "turn_on", "pin": 13})'
                          value={typeof command.payload === 'string' ? command.payload : JSON.stringify(command.payload, null, 2)}
                          onChange={e => updateCommand(index, 'payload', e.target.value)}
                          required
                          rows="3"
                          style={{ 
                            width: '100%', 
                            padding: '0.375rem', 
                            border: '1px solid #ddd', 
                            borderRadius: '4px',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem'
                          }} 
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <select 
                          value={command.buttonColor}
                          onChange={e => updateCommand(index, 'buttonColor', e.target.value)}
                          style={{ padding: '0.375rem', border: '1px solid #ddd', borderRadius: '4px' }}
                        >
                          {buttonColors.map(color => (
                            <option key={color.value} value={color.value}>{color.label}</option>
                          ))}
                        </select>
                        
                        <select 
                          value={command.icon}
                          onChange={e => updateCommand(index, 'icon', e.target.value)}
                          style={{ padding: '0.375rem', border: '1px solid #ddd', borderRadius: '4px' }}
                        >
                          {availableCommandIcons.map(icon => (
                            <option key={icon.value} value={icon.value}>{icon.label}</option>
                          ))}
                        </select>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <input 
                            type="checkbox" 
                            id={`confirm-${index}`}
                            checked={command.confirmRequired}
                            onChange={e => updateCommand(index, 'confirmRequired', e.target.checked)}
                          />
                          <label htmlFor={`confirm-${index}`} style={{ fontSize: '0.875rem' }}>
                            Requiere confirmación
                          </label>
                        </div>
                      </div>

                      {command.confirmRequired && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <input 
                            type="text" 
                            placeholder="Mensaje de confirmación" 
                            value={command.confirmMessage}
                            onChange={e => updateCommand(index, 'confirmMessage', e.target.value)}
                            style={{ 
                              width: '100%', 
                              padding: '0.375rem', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px',
                              fontSize: '0.875rem'
                            }} 
                          />
                        </div>
                      )}

                      <div style={{ marginTop: '0.5rem' }}>
                        <input 
                          type="text" 
                          placeholder="Descripción del comando (opcional)" 
                          value={command.description}
                          onChange={e => updateCommand(index, 'description', e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '0.375rem', 
                            border: '1px solid #ddd', 
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                          }} 
                        />
                      </div>
                    </div>
                  ))}

                  {deviceForm.commands.length === 0 && (
                    <div style={{
                      padding: '1rem',
                      background: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '0.875rem'
                    }}>
                      No hay comandos configurados. Haz clic en "Agregar Comando" para crear uno.
                    </div>
                  )}

                  <div style={{ 
                    marginTop: '1rem',
                    padding: '0.75rem', 
                    background: '#fef3c7', 
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    color: '#92400e'
                  }}>
                    <strong>🎮 Comandos:</strong> Configura acciones que se pueden ejecutar en el dispositivo desde el dashboard.<br/>
                    <strong>📤 Payload JSON:</strong> El contenido que se enviará al tópico MQTT cuando se ejecute el comando.<br/>
                    <strong>🎨 Colores:</strong> Elige el color del botón que aparecerá en el dashboard.<br/>
                    <strong>✅ Confirmación:</strong> Activa para mostrar un diálogo de confirmación antes de ejecutar el comando.
                  </div>
                </div>
                
                <button type="submit" style={{ padding: '0.5rem 1rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', marginRight: '0.5rem' }}>
                  {editingItem ? 'Actualizar' : 'Crear'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '0.5rem 1rem', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px' }}>
                  Cancelar
                </button>
              </form>
            )}
          </div>
        </div>
      )}
      
      {/* Componente de Historial */}
      {showHistory && (
        <DeviceHistory 
          deviceId={selectedDeviceId} 
          onClose={() => setShowHistory(false)} 
        />
      )}

      {/* Panel de Backup Completo */}
      <div style={{
        marginTop: '2rem',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        border: '2px solid #dee2e6',
        borderRadius: '8px'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#495057', textAlign: 'center' }}>
          🗄️ Backup Completo del Sistema
        </h3>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={async () => {
              try {
                const response = await fetch('/api/backup/export/complete', {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                });

                if (!response.ok) throw new Error('Error al exportar');

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                const timestamp = new Date().toISOString().replace(/:/g, '-').replace(/\..+/, '');
                link.download = `configuracion_completa_${timestamp}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              } catch (error) {
                alert('Error al exportar configuración completa: ' + error.message);
              }
            }}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '12px 24px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            📦 Exportar Todo
          </button>

          <label style={{
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '12px 24px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            display: 'inline-block'
          }}>
            📥 Importar Todo
            <input
              type="file"
              accept=".json"
              onChange={async (event) => {
                const file = event.target.files[0];
                if (!file) return;

                try {
                  console.log('📥 Importando configuración completa...');
                  const text = await file.text();
                  const data = JSON.parse(text);

                  if (data.type !== 'complete' || !data.data) {
                    throw new Error('Archivo de backup completo inválido');
                  }

                  const confirmImport = window.confirm(
                    `¿Estás seguro de importar la configuración completa?\n\n` +
                    `Esto importará:\n` +
                    `• ${data.summary?.users || data.data.users?.length || 0} usuarios\n` +
                    `• ${data.summary?.sites || data.data.sites?.length || 0} sitios\n` +
                    `• ${data.summary?.devices || data.data.devices?.length || 0} dispositivos\n\n` +
                    `⚠️ Los elementos existentes se sobrescribirán.`
                  );

                  if (!confirmImport) {
                    event.target.value = '';
                    return;
                  }

                  const overwrite = window.confirm(
                    '¿Sobrescribir elementos existentes con el mismo nombre/email/ID?'
                  );

                  // Importar usuarios
                  if (data.data.users && data.data.users.length > 0) {
                    console.log(`📥 Importando ${data.data.users.length} usuarios...`);
                    await api.post('/api/backup/import/users', {
                      data: data.data.users,
                      overwrite
                    });
                  }

                  // Importar sitios
                  if (data.data.sites && data.data.sites.length > 0) {
                    console.log(`📥 Importando ${data.data.sites.length} sitios...`);
                    await api.post('/api/backup/import/sites', {
                      data: data.data.sites,
                      overwrite
                    });
                  }

                  // Importar dispositivos
                  if (data.data.devices && data.data.devices.length > 0) {
                    console.log(`📥 Importando ${data.data.devices.length} dispositivos...`);
                    await api.post('/api/backup/import/devices', {
                      data: data.data.devices,
                      overwrite
                    });
                  }

                  console.log('✅ Configuración completa importada exitosamente');
                  alert('✅ Configuración completa importada exitosamente\n\nLa página se recargará para mostrar los cambios.');

                  // Recargar toda la configuración
                  await fetchData();

                } catch (error) {
                  console.error('❌ Error importando configuración completa:', error);
                  alert(`Error al importar configuración completa: ${error.message}`);
                } finally {
                  event.target.value = '';
                }
              }}
              style={{ display: 'none' }}
            />
          </label>
        </div>

        <div style={{
          fontSize: '14px',
          color: '#6c757d',
          textAlign: 'center',
          marginTop: '12px',
          maxWidth: '700px',
          margin: '12px auto 0'
        }}>
          💡 <strong>Exportar Todo:</strong> Descarga un archivo con {currentUser.role === 'admin' ? 'usuarios, sitios y dispositivos completos' : 'sitios y dispositivos asignados'}.<br/>
          💡 <strong>Importar Todo:</strong> Restaura {currentUser.role === 'admin' ? 'una configuración completa' : 'sitios y dispositivos asignados'} desde archivo de backup.<br/>
          ⚠️ <strong>Advertencia:</strong> La importación sobrescribe la configuración actual.
        </div>
      </div>

    </div>
  );
};

export default Configuration;